<template>
  <div class="max-w-6xl p-6 mx-auto md:p-8">
    <!-- Page header -->
    <div>
      <h2 class="text-2xl font-semibold text-gray-800">Push Notification</h2>
      <p class="text-sm text-gray-500">
        Send announcements to your users and review previously sent messages
      </p>
    </div>

    <div class="mt-6 space-y-4">
      <!-- Compose card -->
      <section class="bg-white border border-gray-200 rounded-2xl shadow-sm">
        <header class="flex items-start px-6 py-4 border-b border-gray-100">
          <span
            class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-4 text-taxiye-500 bg-taxiye-50 rounded-xl material-icons-round"
          >
            campaign
          </span>
          <div>
            <h3 class="text-base font-semibold text-gray-800">
              Send a Push Notification
            </h3>
            <p class="text-sm text-gray-500">
              Quickly send out a push notification to your users!
            </p>
          </div>
        </header>
        <form @submit.prevent="send" class="p-6">
          <div class="grid grid-cols-1 gap-x-4 gap-y-5 md:grid-cols-2">
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                To
              </span>
              <select
                @change="setAudience"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
              >
                <option value="driver">All Drivers</option>
                <option value="passenger">All Passengers</option>
                <option value="all">All</option>
              </select>
            </label>
            <label class="block">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Medium
              </span>
              <select
                v-model="medium"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
              >
                <option value="push">Push Notification</option>
                <option value="sms">SMS</option>
                <option value="both">Both</option>
              </select>
            </label>
            <label class="block md:col-span-2">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Title
              </span>
              <input
                v-model="title"
                class="block w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="Fare change"
              />
            </label>
            <label class="block md:col-span-2">
              <span class="text-xs font-semibold tracking-wider text-gray-600 uppercase">
                Message
              </span>
              <textarea
                v-model="body"
                class="block w-full mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-textarea rounded-xl focus:border-taxiye-300"
                rows="3"
                placeholder="Push notification message..."
              ></textarea>
            </label>
          </div>
          <div class="flex justify-end mt-5">
            <button
              type="submit"
              :disabled="busy"
              class="inline-flex items-center h-10 px-8 text-sm font-semibold text-white rounded-xl shadow-sm transition-colors duration-150"
              :class="
                busy
                  ? 'bg-taxiye-300 cursor-not-allowed'
                  : 'bg-taxiye-500 hover:bg-taxiye-600'
              "
            >
              <span class="mr-1.5 text-base material-icons-round">send</span>
              {{ busy ? "Sending..." : "Send" }}
            </button>
          </div>
        </form>
      </section>

      <!-- History card -->
      <section class="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm">
        <div class="flex items-center mb-4 space-x-1 text-gray-500">
          <span class="text-base material-icons-round">history</span>
          <span class="text-xs font-semibold tracking-widest uppercase">
            Sent Notifications
          </span>
        </div>

        <!-- Filters -->
        <div class="flex flex-wrap items-end gap-4">
          <div class="relative w-full sm:w-64">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Search
            </label>
            <div class="relative mt-1.5">
              <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <span class="text-base text-gray-500 material-icons-round">search</span>
              </span>
              <input
                class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                v-model="searchKey"
                placeholder="Search notifications..."
                @keyup="search"
              />
            </div>
          </div>
          <div class="w-full sm:w-48">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">
              Audience
            </label>
            <select
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
              v-model="type"
              @change="getNotifications"
            >
              <option value="all">All</option>
              <option value="passenger">Passenger</option>
              <option value="driver">Driver</option>
            </select>
          </div>
        </div>

        <!-- Table -->
        <div class="relative mt-4 overflow-hidden border border-gray-200 rounded-xl">
          <div class="overflow-x-auto">
            <table class="min-w-full text-sm">
              <thead>
                <tr class="bg-gray-100 border-b border-gray-200">
                  <th
                    v-for="col in [
                      'No #',
                      'Sent To',
                      'Message',
                      'Sent On',
                      'Status',
                      'Medium',
                    ]"
                    :key="`col-${col}`"
                    class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase whitespace-no-wrap"
                  >
                    {{ col }}
                  </th>
                </tr>
              </thead>
              <tbody v-if="notifications.length">
                <tr
                  v-for="(notification, index) in notifications"
                  :key="notification._id"
                  class="transition-colors duration-150 hover:bg-taxiye-50"
                  :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-100'"
                >
                  <td class="px-5 py-4 border-b border-gray-200">
                    <p class="text-xs text-gray-500 whitespace-no-wrap">
                      {{ notification._id }}
                    </p>
                  </td>
                  <td class="px-5 py-4 border-b border-gray-200">
                    <p class="text-gray-800 whitespace-no-wrap">
                      {{ toString(notification.to) }}
                    </p>
                  </td>
                  <td class="px-5 py-4 border-b border-gray-200">
                    <p class="font-semibold text-gray-800 whitespace-no-wrap">
                      {{ notification.title }}
                    </p>
                    <p class="text-gray-500 whitespace-no-wrap">
                      {{ notification.body }}
                    </p>
                  </td>
                  <td class="px-5 py-4 border-b border-gray-200">
                    <p class="text-gray-800 whitespace-no-wrap">
                      {{ fromNow(notification.createdAt) }}
                    </p>
                    <p class="text-xs text-gray-500 whitespace-no-wrap">
                      {{ formatDate(notification.createdAt) }}
                    </p>
                  </td>
                  <td class="px-5 py-4 border-b border-gray-200">
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full"
                      :class="statusClass(notification.status)"
                    >
                      {{ notification.status.toUpperCase() }}
                    </span>
                  </td>
                  <td class="px-5 py-4 border-b border-gray-200">
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 text-xs font-semibold text-gray-600 bg-gray-200 rounded-full"
                    >
                      {{ notification.medium.toUpperCase() }}
                    </span>
                  </td>
                </tr>
              </tbody>
              <tbody v-else class="bg-white">
                <tr>
                  <td colspan="6">
                    <div class="flex flex-col items-center py-12 text-gray-400">
                      <span class="text-4xl material-icons-round">
                        notifications_off
                      </span>
                      <p class="mt-2 text-sm font-semibold uppercase">
                        No notifications
                      </p>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-if="loading"
            class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75"
          >
            <i class="text-4xl text-taxiye-500 material-icons-round spin">sync</i>
            <span class="invisible block">Loading...</span>
          </div>
        </div>

        <!-- Pagination -->
        <div class="flex items-end justify-between mt-4">
          <pagination
            class="flex space-x-2"
            :currentPage="currentPage"
            :total="total"
            @goto="goToPage"
            @setPerPage="setPerPage"
            :perPage="perPage"
            :start="start"
            :end="end"
            :count="paginator.count"
          ></pagination>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import moment from "moment";
import Pagination from "@/components/Pagination";
import PaginationMixin from "@/mixins/Pagination";

export default {
  mixins: [PaginationMixin],
  components: {
    Pagination
  },
  data() {
    return {
      busy: false,
      to: "driver",
      title: "",
      type: "all",
      searchKey: "",
      loading: true,
      body: "",
      notifications: [],
      change: "",
      medium: "push"
    };
  },
  created() {
    this.getNotifications();
  },
  watch: {
    currentPage() {
      this.getNotifications();
    },
    perPage() {
      this.getNotifications();
    }
  },
  methods: {
    getNotifications() {
      this.loading = true;
      var path = `/notifications?limit=${this.perPage}&page=${this.currentPage}`;

      if (this.searchKey) {
        path += "&q=" + this.searchKey;
      }

      if (this.type) {
        path += "&to=" + this.type;
      }

      this.$misc
        .get(path)
        .then(response => {
          this.notifications = response.data.data;
          this.paginator = {
            nextPage: response.data.nextPage,
            prevPage: response.data.prevPage,
            count: response.data.count
          };
          this.loading = false;
        })
        .catch(err => console.log(err));
    },
    setAudience(e) {
      if (["driver", "passenger", "all"].includes(e.target.value)) {
        this.to = e.target.value;
      }
    },
    search: _.debounce(function(e) {
      this.searchKey = e.target.value.trim();
      this.getNotifications();
    }, 500),
    send() {
      if (!this.to) return;
      this.busy = true;

      this.$misc
        .post(`/notifications/topic/${this.to}`, {
          title: this.title,
          body: this.body,
          medium: this.medium,
        })
        .then(() => {
          window.flash({
            type: "success",
            body: "Notification sent successfully!"
          });
          this.getNotifications();
        })
        .catch(err => console.log(err))
        .finally(() => (this.busy = false));
    },
    formatDate(date) {
      return date ? date.replace("T", " ").slice(0, 16) : "-";
    },
    fromNow(date) {
      return moment(date).fromNow();
    },
    statusClass(status) {
      const s = (status || "").toLowerCase();
      if (["sent", "success", "delivered", "completed"].includes(s)) {
        return "text-green-700 bg-green-100";
      }
      if (["failed", "error"].includes(s)) {
        return "text-red-700 bg-red-100";
      }
      return "text-yellow-700 bg-yellow-100";
    },
    toString(to) {
      if (to === "driver") {
        return "All Drivers";
      }
      if (to === "passenger") {
        return "All Passengers";
      }
      if (to === "all") {
        return "All Users";
      }
    }
  }
};
</script>
